import API from 'src/api'
import { http } from '../boot/axios'
import { Capacitor } from '@capacitor/core'

async function normalizeTaskImage (image) {
  if (!Capacitor.isNativePlatform()) {
    return image
  }

  if (typeof image === 'string' && image.startsWith('data:image')) {
    return image
  }

  if (typeof image === 'string' && image.startsWith('file://')) {
    const { loadAndCompress } =
      await import('src/services/offline-images')

    const base64 = await loadAndCompress(image, 1200)

    if (typeof base64 === 'string' && base64.startsWith('data:image')) {
      return base64
    }

    return null
  }

  return image
}

async function normalizePictures (task) {
  if (!Capacitor.isNativePlatform()) return task
  if (!task || !Array.isArray(task.pictures)) return task

  const pictures = []

  for (const img of task.pictures) {
    const normalized = await normalizeTaskImage(img)
    if (normalized) pictures.push(normalized)
  }

  return {
    ...task,
    pictures
  }
}


export default {
  GRAPHQL_URL: API.GRAPHQL_URL + 'tasks',

  find (id) {
    // Sin id NO hay request: `GET tasks/` (colección) no existe como GET en el backend y responde
    // 405 booteando Laravel completo. Rechazar local es gratis; el request basura no (incidente
    // CPU 2026-08-14).
    if (id === undefined || id === null || id === '') {
      return Promise.reject(new Error('tasks.find: id requerido'))
    }
    return new Promise((resolve, reject) => {
      if (!id) {
        reject(new Error('tasksService.find: id vacio o indefinido, peticion cancelada'))
        return
      }
      http.get(`${this.GRAPHQL_URL}/${id}`)
        .then(res => resolve(res.data))
        .catch(err => reject(err))
    })
  },
  createTask (task) {
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          const normalizedTask = await normalizePictures(task)

          http.post(`${this.GRAPHQL_URL}`, normalizedTask)
            .then(res => resolve(res.data.data))
            .catch(err => reject(err))
        } catch (e) {
          reject(e)
        }
      })()
    })
  },
  deleteTask (taskId) {
    return new Promise((resolve, reject) => {
      http.delete(`${this.GRAPHQL_URL}/${taskId}`)
        .then(res => resolve(res.data.data))
        .catch(err => reject(err))
    })
  },
  deleteOccTask (occurrenceId) {
    return new Promise((resolve, reject) => {
      http.get(`${this.GRAPHQL_URL}/${occurrenceId}/clearOcc`)
        .then(res => resolve(res.data.data))
        .catch(err => reject(err))
    })
  },
  createTaskEditing (task) {
    task.state = 'editing'
    return new Promise((resolve, reject) => {
      http.post(`${this.GRAPHQL_URL}/fromForms`, task)
        // fromForms responde { success: task }, NO { data: task } -> resolvemos el body completo para que
        // el caller pueda leer el id (res.success.id). El único otro caller (createAutoActionPlanTask) ignora el valor.
        .then(res => resolve(res.data))
        .catch(err => reject(err))
    })
  },
  createMultipleTasks (task) {
    return new Promise((resolve, reject) => {
      http.post(`${this.GRAPHQL_URL}/multiples`, task)
        .then(res => resolve(res.data))
        .catch(err => reject(err))
    })
  },
  updateTask (task) {
    return new Promise((resolve, reject) => {
      http.post(`${this.GRAPHQL_URL}/changeStatus`, task)
        .then(res => resolve(res.data.data))
        .catch(err => reject(err))
    })
  },
  updateFilds (task) {
    return new Promise((resolve, reject) => {
      http.post(`${this.GRAPHQL_URL}/updateFilds`, task)
        .then(res => resolve(res.data.data))
        .catch(err => reject(err))
    })
  },
  updateFildImages (payload) {
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          if (payload && payload.content) {
            const normalized = await normalizeTaskImage(payload.content)
            payload = { ...payload, content: normalized }
          }

          http.post(`${this.GRAPHQL_URL}/updateFildImages`, payload)
            .then(res => resolve(res))
            .catch(err => reject(err))
        } catch (e) {
          reject(e)
        }
      })()
    })
  },
  insertTaskMedia (taskId, media) {
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          if (media && media.picture_evidence) {
            const normalized = await normalizeTaskImage(media.picture_evidence)
            media = { ...media, picture_evidence: normalized }
          }

          http.post(`${this.GRAPHQL_URL}/answer/${taskId}`, media)
            .then(res => resolve(res.data.data))
            .catch(err => reject(err))
        } catch (e) {
          reject(e)
        }
      })()
    })
  },
  fetchFiltered (payload) {
    return new Promise((resolve, reject) => {
      http.get(`${this.GRAPHQL_URL}/filter`, { params: payload })
        .then(res => resolve(res.data))
        .catch(err => reject(err))
    })
  },
  reassignTask (payload) {
    return new Promise((resolve, reject) => {
      http.put(`${this.GRAPHQL_URL}/reassignTask/${payload.id}`, payload)
        .then(response => { resolve(response.data) })
        .catch(error => { reject(error) })
    })
  },
  changeStatus (payload) {
    return new Promise((resolve, reject) => {
      http.post(`${this.GRAPHQL_URL}/changeStatus`, payload)
        .then(response => { resolve(response.data) })
        .catch(error => { reject(error) })
    })
  },
  findById (id) {
    // Mismo guard que find(): sin id no hay request (ver comentario ahí).
    if (id === undefined || id === null || id === '') {
      return Promise.reject(new Error('tasks.findById: id requerido'))
    }
    return new Promise((resolve, reject) => {
      if (!id) {
        reject(new Error('tasksService.findById: id vacio o indefinido, peticion cancelada'))
        return
      }
      http.get(`${this.GRAPHQL_URL}/${id}`)
        .then(response => { resolve(response.data) })
        .catch(error => { reject(error) })
    })
  },
  fetchFilteredAreas (categoryName, config = {}) {
    return new Promise((resolve, reject) => {
      http.get(`${API.GRAPHQL_URL}fetchAreas`, {
        params: { category: categoryName },
        ...config
      })
        .then(response => { resolve(response.data) })
        .catch(error => { reject(error) })
    })
  }
}
